import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Wine, Search, Sparkles, Crown, Plus, Check, ShoppingBag, MapPin } from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { TEQUILA_LINEUP } from '../data/tequilaLineup';
import { useReservationCart } from './reservation/ReservationCartContext';

const CATEGORIES = ['All', 'Tequila', 'Mezcal', 'Sotol'];

const TequilaListPage = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [vipOnly, setVipOnly] = useState(false);
  const cart = useReservationCart();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEQUILA_LINEUP
      .filter((b) => category === 'All' || b.category === category)
      .filter((b) => !vipOnly || b.expressions.some((e) => e.vip))
      .map((b) => vipOnly ? { ...b, expressions: b.expressions.filter((e) => e.vip) } : b)
      .filter((b) =>
        !q ||
        b.name.toLowerCase().includes(q) ||
        b.expressions.some((e) => e.name.toLowerCase().includes(q))
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [query, category, vipOnly]);

  const counts = useMemo(() => ({
    All: TEQUILA_LINEUP.length,
    Tequila: TEQUILA_LINEUP.filter((b) => b.category === 'Tequila').length,
    Mezcal:  TEQUILA_LINEUP.filter((b) => b.category === 'Mezcal').length,
    Sotol:   TEQUILA_LINEUP.filter((b) => b.category === 'Sotol').length,
  }), []);

  const totals = useMemo(() => {
    const expressions = TEQUILA_LINEUP.reduce((acc, b) => acc + b.expressions.length, 0);
    const vipExpressions = TEQUILA_LINEUP.reduce((acc, b) => acc + b.expressions.filter((e) => e.vip).length, 0);
    return { expressions, vipExpressions };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a14] via-[#0a0a0a] to-black text-white" data-testid="tequila-list-page">
      {/* Header */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-orange-500/5 to-amber-500/10 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-12 sm:pb-16">
          <Link
            to="/"
            data-testid="tequila-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-pink-400 text-sm font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to homepage
          </Link>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30">
              <Wine className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <Badge className="bg-pink-500/15 text-pink-300 border border-pink-500/30 hover:bg-pink-500/20">
              <Sparkles className="w-3 h-3 mr-1" />
              {TEQUILA_LINEUP.length} brands · {totals.expressions} pours
            </Badge>
            <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20">
              <Crown className="w-3 h-3 mr-1" />
              {totals.vipExpressions} VIP-only pours
            </Badge>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4">
            <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
              The Tequila Lineup
            </span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
            Every premium tequila, mezcal, and sotol confirmed to pour at the 1st Annual
            Aloha Tequila &amp; Arts Festival on Saturday, June 20, 2026 — with direct links to each brand.
            Look for the <Crown className="w-3.5 h-3.5 inline -mt-0.5 text-amber-400" /> badge to spot VIP-exclusive pours.
          </p>

          {/* Reserve banner */}
          <div className="mt-6 flex items-start gap-3 bg-gradient-to-r from-pink-500/15 via-orange-500/10 to-amber-500/15 border border-pink-500/30 rounded-2xl px-4 sm:px-5 py-3.5 max-w-3xl">
            <div className="w-9 h-9 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center shrink-0 shadow-lg shadow-pink-500/30">
              <ShoppingBag className="w-4 h-4 text-white" />
            </div>
            <div className="text-sm text-white/85 leading-relaxed">
              <strong className="text-white">Want to take a bottle home?</strong> Order onsite at the festival and pick up at:
              <span className="block mt-1.5 text-white/80">
                <span className="inline-flex items-center gap-1.5 mr-3"><MapPin className="w-3 h-3 text-pink-300" /><strong className="text-pink-300">Tamura&apos;s</strong> on 10th Ave</span>
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-pink-300" /><strong className="text-pink-300">Fujioka&apos;s</strong> · Kapolei</span>
              </span>
              <span className="text-white/55 text-xs"> Pay at any check-in or either of the main bars. Subject to availability · must be 21+ with valid ID at pickup.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col gap-4 mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            <Input
              data-testid="tequila-search"
              type="text"
              placeholder="Search brand or expression (e.g. Reposado, Mezcal, Ocho)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-11 bg-white/5 border-white/15 text-white placeholder:text-white/40 h-12 rounded-full focus-visible:border-pink-500/50 focus-visible:ring-pink-500/20"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                data-testid={`tequila-filter-${cat.toLowerCase()}`}
                onClick={() => setCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  category === cat
                    ? 'bg-gradient-to-r from-pink-500 to-orange-500 text-white shadow-lg shadow-pink-500/30'
                    : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                {cat} <span className="ml-1 text-xs opacity-70">({counts[cat]})</span>
              </button>
            ))}
            <button
              type="button"
              data-testid="tequila-filter-vip"
              onClick={() => setVipOnly((v) => !v)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                vipOnly
                  ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-black shadow-lg shadow-amber-500/30'
                  : 'bg-white/5 text-amber-300 border border-amber-500/30 hover:bg-amber-500/10'
              }`}
            >
              <Crown className="w-4 h-4" />
              VIP-only pours <span className="text-xs opacity-70">({totals.vipExpressions})</span>
            </button>
          </div>
        </div>

        {/* Brand Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50">
            No matches — try a different search.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="tequila-grid">
            {filtered.map((brand) => {
              const slug = brand.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
              const hasVip = brand.expressions.some((e) => e.vip);
              return (
                <article
                  key={brand.name}
                  data-testid={`tequila-card-${slug}`}
                  className={`group relative bg-white/[0.04] hover:bg-white/[0.07] border rounded-2xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col ${
                    hasVip
                      ? 'border-amber-500/30 hover:border-amber-400/60 hover:shadow-amber-500/10'
                      : 'border-white/10 hover:border-pink-500/40 hover:shadow-pink-500/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
                      {brand.name}
                    </h3>
                    <Badge className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                      brand.category === 'Tequila'
                        ? 'bg-pink-500/15 text-pink-300 border border-pink-500/30'
                        : brand.category === 'Mezcal'
                        ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                        : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
                    }`}>
                      {brand.category}
                    </Badge>
                  </div>

                  {brand.table && (
                    <Link
                      to="/map"
                      data-testid={`tequila-table-${slug}`}
                      title="Open the floor map"
                      className={`inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-bold px-2.5 py-1 rounded-full mb-3 transition-colors ${
                        brand.table.startsWith('VIP')
                          ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25'
                          : 'bg-pink-500/10 text-pink-300 border border-pink-500/25 hover:bg-pink-500/20'
                      }`}
                    >
                      <MapPin className="w-3 h-3" />
                      {brand.table}
                    </Link>
                  )}

                  <div className="flex flex-wrap gap-1.5 mb-4 flex-1">
                    {brand.expressions.map((expr) => {
                      const inCart = cart.has(brand.name, expr.name);
                      const exprSlug = expr.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                      return (
                        <button
                          type="button"
                          key={expr.name}
                          data-testid={`reserve-${slug}-${exprSlug}`}
                          onClick={() => cart.addItem({ brand: brand.name, expression: expr.name, price: expr.price, vip: !!expr.vip })}
                          title={inCart ? 'Already in your reservation — click to add another' : `Reserve ${brand.name} ${expr.name} for festival pickup`}
                          className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 transition-all cursor-pointer active:scale-95 ${
                            inCart
                              ? 'bg-green-500/20 text-green-200 border-green-500/50 font-semibold shadow-sm shadow-green-500/20'
                              : expr.vip
                              ? 'bg-amber-500/15 text-amber-200 border-amber-500/40 font-semibold hover:bg-amber-500/25 hover:border-amber-400/60'
                              : 'bg-white/5 text-white/80 border-white/10 hover:bg-pink-500/15 hover:border-pink-500/40 hover:text-pink-200'
                          }`}
                        >
                          {inCart ? (
                            <Check className="w-3 h-3" />
                          ) : expr.vip ? (
                            <Crown className="w-3 h-3" />
                          ) : (
                            <Plus className="w-3 h-3" />
                          )}
                          <span>{expr.name}</span>
                          {typeof expr.price === 'number' && (
                            <span className={`tabular-nums font-bold ${inCart ? 'text-green-300' : expr.vip ? 'text-amber-300' : 'text-pink-300'}`}>
                              ${expr.price.toFixed(2).replace(/\.00$/, '')}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {brand.website ? (
                    <a
                      href={brand.website}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      data-testid={`tequila-website-${slug}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-pink-400 hover:text-pink-300 transition-colors group-hover:gap-2"
                    >
                      Visit website
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <span className="text-xs text-white/40 italic">Website coming soon</span>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {/* VIP nudge */}
        {!vipOnly && (
          <div className="mt-12 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-pink-500/10 border border-amber-500/30 rounded-3xl p-6 sm:p-8 text-center max-w-3xl mx-auto">
            <Crown className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
              {totals.vipExpressions} ultra-premium pours are VIP-only
            </h3>
            <p className="text-white/70 text-sm sm:text-base mb-5 max-w-xl mx-auto">
              Cuervo Reserva de la Familia, Komos Cristalino, Partida Roble Fino, Milagro Barrel Select,
              Herradura Legend Añejo, Dobel 50, LALO Blanco — only in the VIP area.
            </p>
            <a
              href="https://alohacancer.ticketspice.com/aloha-tequila-arts-festival"
              data-testid="tequila-vip-cta"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-black font-black text-sm sm:text-base px-6 py-3 rounded-full shadow-xl shadow-amber-500/30 transition-all hover:scale-105"
            >
              <Crown className="w-5 h-5" />
              Upgrade to VIP — $110
            </a>
            <p className="text-white/40 text-xs mt-3">Prices go up June 7 to $125. Tickets selling fast.</p>
          </div>
        )}

        {/* Footnote */}
        <p className="text-center text-xs text-white/40 mt-10 max-w-2xl mx-auto leading-relaxed">
          Prices shown are <strong className="text-white/60">approximate retail bottle prices (750ml)</strong> at Tamura&apos;s Fine Wine &amp; Liquors —
          for reference only. Pours at the festival are unlimited 1-oz samples included with your ticket.
          Lineup subject to change. Final pouring list confirmed week-of June 20, 2026.
          21+ event · Drink responsibly · Tickets are non-refundable.
        </p>
      </div>
    </div>
  );
};

export default TequilaListPage;
